import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { I18nextContext } from 'gatsby-plugin-react-i18next';
import { useContext } from 'react';
import { GetSeoMetaQuery } from '../../graphql-types';

export const SEO_QUERY = graphql`
    query getSEOMeta {
        siteSettings: sanitySiteSettings {
            openGraph {
                title
                image {
                    asset {
                        gatsbyImageData(
                            fit: FILLMAX
                            placeholder: DOMINANT_COLOR
                        )
                    }
                }
                description {
                    en
                    es
                }
            }
        }
    }
`;

interface SEOProps {
    description?: string;
    meta?: Array<any>;
    title?: string;
    image?: any;
}

function SEO({ description = '', meta = [], title = '', image }: SEOProps) {
    const context = useContext(I18nextContext);
    const lang = context.language;
    const { siteSettings }: GetSeoMetaQuery = useStaticQuery(SEO_QUERY);

    const metaDescription =
        description || siteSettings?.openGraph?.description?.[lang] || '';
    const defaultTitle =
        siteSettings?.openGraph?.title?.[lang] || 'En Base Studio';

    const metaTitle = title || defaultTitle;

    const ogImage =
        image ?? siteSettings?.openGraph?.image?.asset?.gatsbyImageData;

    return (
        <Helmet
            htmlAttributes={{
                lang,
            }}
            title={metaTitle}
            titleTemplate={defaultTitle ? `%s | ${defaultTitle}` : null}
            meta={[
                {
                    name: `description`,
                    content: metaDescription,
                },
                {
                    property: `og:title`,
                    content: metaTitle,
                },
                {
                    property: `og:description`,
                    content: metaDescription,
                },
                {
                    property: `og:type`,
                    content: `website`,
                },
                {
                    name: 'og:image',
                    content: ogImage.images.fallback.src,
                },

                {
                    name: 'og:image:width',
                    content: `${ogImage.width}`,
                },
                {
                    name: 'og:image:height',
                    content: `${ogImage.height}`,
                },
                {
                    name: 'twitter:image',
                    content: ogImage.images.fallback.src,
                },
                {
                    name: `twitter:card`,
                    content: `summary`,
                },
                {
                    name: `twitter:title`,
                    content: metaTitle,
                },

                {
                    name: `twitter:description`,
                    content: metaDescription,
                },
            ].concat(meta)}
        />
    );
}

export default SEO;
